import React, { useState, useMemo, useCallback } from "react";
import { CardData, FolderGroup } from "@/components/layout/element-layout/grid-layout/GridLayout";
import MainTableWidget from "./MainTableWidget";
import { transformCardsToTableData } from "@/lib/utils/cardToTableTransformer";
import IconProvider from "@/components/ui/IconProvider";
import { SectionWithFields } from "./TableColumnSelector";
import { ColumnOrderDialog } from "./ColumnOrderDialog";
import { useAuthStore } from "@/lib/api-functions/api-auth";
import { getDefaultColumnsForItemType, getFieldMetadataForItemType } from "@/lib/dataSources/shared/tableColumns";
import { ShadowMode } from "@/components/cards/widgets/TableFilter/types";
import { getShadowProps } from "@/components/layout/element-layout/grid-layout/shadowUtils";
import { CsvExportButton } from "@/components/ui/CsvExportButton";
import {
  exportFolderGroupToCsv,
  exportAllFolderGroupsToCsv,
  CsvExportConfig,
} from "@/lib/utils/csvExport";

interface FolderGroupedTableDisplayProps {
  groupedCards: FolderGroup[];
  itemType: string;
  selectedFolderId?: string | null;
  onCardClick?: (card: CardData) => void;
  // Raw items with full data for extracting sections/fields
  rawItems?: any[];
  // Template lookup for getting template info
  templateLookup?: Record<string, { id: string; name: string; color?: string }>;
  // Callback when a cell value changes in table view
  onCellValueChange?: (rowKey: string, cellKey: string, newValue: any) => void;
  // Pending changes props
  modifiedItems?: Set<string>;
  modifiedCells?: Map<string, string[]>;
  onSaveItem?: (itemId: string) => void;
  onDiscardItem?: (itemId: string) => void;
  // Folder path lookup function for displaying folder paths instead of IDs
  folderPathLookup?: (folderId: string | null | undefined) => string;
  // Shadow mode for determining left border color
  shadowMode?: ShadowMode;
  // Lifted column selection state - managed by parent to persist across data updates
  groupColumnSelections?: Record<string, string[]>;
  onGroupColumnSelectionsChange?: (selections: Record<string, string[]>) => void;
  // Selected rows state for export functionality
  selectedRowsByGroup?: Record<string, Set<string>>;
  onSelectedRowsChange?: (selections: Record<string, Set<string>>) => void;
}

/**
 * Component that displays cards grouped by folder in table format
 * 
 * - Root level: Each folder gets its own collapsible table
 * - Inside a folder: Shows parent folder section with direct items and nested subfolders
 * - Each table has its own column selector dropdown
 */
export function FolderGroupedTableDisplay({
  groupedCards,
  itemType,
  selectedFolderId = null,
  onCardClick,
  rawItems = [],
  templateLookup = {},
  onCellValueChange,
  modifiedItems = new Set(),
  modifiedCells = new Map(),
  onSaveItem,
  onDiscardItem,
  folderPathLookup,
  shadowMode = "folder",
  groupColumnSelections: externalColumnSelections,
  onGroupColumnSelectionsChange,
  selectedRowsByGroup: externalSelectedRows,
  onSelectedRowsChange,
}: FolderGroupedTableDisplayProps) {
  const { user } = useAuthStore();
  const currentUserId = user?.id;
  
  // Debug: Log current user ID for lock verification
  console.log('[FolderGroupedTableDisplay] Current user ID:', currentUserId);
  
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  
  // Per-group column selection state - use external state if provided, otherwise use local state
  // External state is lifted to parent to persist across data updates (e.g., when saving a field)
  const [localColumnSelections, setLocalColumnSelections] = useState<Record<string, string[]>>({});
  
  // Use external state if provided (controlled mode), otherwise use local state (uncontrolled mode)
  const groupColumnSelections = externalColumnSelections ?? localColumnSelections;

  // Per-group selected rows state - use external state if provided, otherwise use local state
  const [localSelectedRows, setLocalSelectedRows] = useState<Record<string, Set<string>>>({});
  
  // Use external state if provided (controlled mode), otherwise use local state (uncontrolled mode)
  const selectedRowsByGroup = externalSelectedRows ?? localSelectedRows;
  
  // Default columns - all metadata columns for this item type
  const defaultColumns = useMemo(() => getDefaultColumnsForItemType(itemType), [itemType]);
  
  // Field metadata for this item type
  const fieldMetadata = useMemo(() => getFieldMetadataForItemType(itemType), [itemType]);
  
  // Build template lookup with sections from raw items
  const enrichedTemplateLookup = useMemo(() => {
    const lookup: Record<string, { id: string; name: string; color?: string; sections?: SectionWithFields[] }> = {};
    
    // Start with existing template lookup
    Object.entries(templateLookup).forEach(([id, template]) => {
      lookup[id] = { ...template };
    });
    
    // Extract sections from raw items and add to lookup
    rawItems.forEach((item: any) => {
      if (item.blueprint_id && Array.isArray(item.data) && !lookup[item.blueprint_id]?.sections) {
        const sections: SectionWithFields[] = item.data.map((section: any) => ({
          id: section.id,
          title: section.title || 'Untitled Section',
          fields: (section.fields || []).map((field: any) => ({
            id: field.id,
            label: field.label || 'Untitled Field',
            type: field.type || 'text',
          })),
        }));
        
        if (lookup[item.blueprint_id]) {
          lookup[item.blueprint_id].sections = sections;
        } else {
          lookup[item.blueprint_id] = {
            id: item.blueprint_id,
            name: 'Unknown Template',
            sections,
          };
        }
      }
    });
    
    return lookup;
  }, [templateLookup, rawItems]);

  // Create a blueprints map for ID to name lookup
  const blueprintsMap = useMemo(() => {
    const map = new Map<string, string>();
    Object.entries(templateLookup).forEach(([id, template]) => {
      map.set(id, template.name);
    });
    return map;
  }, [templateLookup]);

  // CSV export configuration
  const csvExportConfig: CsvExportConfig = useMemo(() => ({
    folderPathLookup,
    blueprintsMap,
  }), [folderPathLookup, blueprintsMap]);

  const toggleFolderCollapse = useCallback((folderId: string | null) => {
    const key = folderId || "unfiled";
    setCollapsedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // Get raw items for a specific folder group
  const getRawItemsForGroup = useCallback((group: FolderGroup): any[] => {
    return rawItems.filter((item: any) => {
      const cardIds = group.cards.map(c => c.id);
      return cardIds.includes(item.id);
    });
  }, [rawItems]);

  // Handle column selection change for a group
  const handleColumnsChange = useCallback((folderId: string | null, columns: string[]) => {
    const key = folderId || "unfiled";
    if (onGroupColumnSelectionsChange) {
      // Controlled mode - call the external handler with the new state
      onGroupColumnSelectionsChange({
        ...groupColumnSelections,
        [key]: columns,
      });
    } else {
      // Uncontrolled mode - update local state
      setLocalColumnSelections(prev => ({
        ...prev,
        [key]: columns,
      }));
    }
  }, [onGroupColumnSelectionsChange, groupColumnSelections]);

  // Handle selected rows change for a group
  const handleSelectedRowsChange = useCallback((folderId: string | null, selectedRows: Set<string>) => {
    const key = folderId || "unfiled";
    if (onSelectedRowsChange) {
      // Controlled mode - call the external handler with the new state
      onSelectedRowsChange({
        ...selectedRowsByGroup,
        [key]: selectedRows,
      });
    } else {
      // Uncontrolled mode - update local state
      setLocalSelectedRows(prev => ({
        ...prev,
        [key]: selectedRows,
      }));
    }
  }, [onSelectedRowsChange, selectedRowsByGroup]);

  // Export handlers for individual folder groups
  const handleExportGroup = useCallback((
    group: FolderGroup,
    selectedColumns: string[],
    exportSelectedOnly: boolean
  ) => {
    const folderKey = group.folderId || "unfiled";
    const groupRawItems = getRawItemsForGroup(group);
    const groupSelectedRows = selectedRowsByGroup[folderKey] || new Set<string>();
    
    exportFolderGroupToCsv({
      group,
      selectedColumns,
      rawItems: groupRawItems,
      selectedRows: groupSelectedRows,
      exportSelectedOnly,
      config: csvExportConfig,
    });
  }, [getRawItemsForGroup, selectedRowsByGroup, csvExportConfig]);

  // Export handler for all groups
  const handleExportAll = useCallback((exportSelectedOnly: boolean) => {
    exportAllFolderGroupsToCsv({
      groups: groupedCards,
      getSelectedColumnsForGroup: (folderKey) => groupColumnSelections[folderKey] || defaultColumns,
      getRawItemsForGroup,
      selectedRowsByGroup,
      exportSelectedOnly,
      config: csvExportConfig,
    });
  }, [groupedCards, groupColumnSelections, defaultColumns, getRawItemsForGroup, selectedRowsByGroup, csvExportConfig]);

  // Check if any rows are selected across all groups
  const hasAnySelection = useMemo(() => {
    return Object.values(selectedRowsByGroup).some(set => set.size > 0);
  }, [selectedRowsByGroup]);

  // Count total selected rows
  const totalSelectedCount = useMemo(() => {
    return Object.values(selectedRowsByGroup).reduce((sum, set) => sum + set.size, 0);
  }, [selectedRowsByGroup]);

  if (groupedCards.length === 0) {
    return null;
  }

  // Render each folder group as a separate section
  // Only show inline badges when cards have subfolderName (sub-subfolder items)
  return (
    <div className="space-y-6">
     
      
      {groupedCards.map((group) => {
        const folderKey = group.folderId || "unfiled";
        const isCollapsed = collapsedFolders.has(folderKey);
        const selectedColumns = groupColumnSelections[folderKey] || defaultColumns;
        const groupRawItems = getRawItemsForGroup(group);
        const groupSelectedRows = selectedRowsByGroup[folderKey] || new Set<string>();
        
        return (
          <div key={folderKey} className="folder-group">
            {/* Folder Header with Column Selector */}
            <div className="mb-4 flex items-center gap-2">
              <div 
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => toggleFolderCollapse(group.folderId)}
              >
                <IconProvider
                  iconstring={isCollapsed ? "fa-chevron-right" : "fa-chevron-down"}
                  size={12}
                  className="text-sidebar-foreground ml-2 mr-2"
                />
                <div className="text-base font-medium text-sidebar-foreground">
                  {`${group.folderName} (${group.cards.length})`}
                </div>
              </div>
              
              {/* Column Manager and Export - positioned next to the name, hidden when collapsed */}
              {!isCollapsed && (
                <>
                  {/* Column Manager Dialog - handles visibility and ordering */}
                  <ColumnOrderDialog
                    selectedColumns={selectedColumns}
                    onColumnsChange={(columns) => handleColumnsChange(group.folderId, columns)}
                    itemType={itemType}
                    sections={(() => {
                      // Get sections from the template if all items share the same template
                      const templateIds = new Set(groupRawItems.map((item: any) => item.blueprint_id).filter(Boolean));
                      if (templateIds.size === 1) {
                        const templateId = Array.from(templateIds)[0];
                        const template = enrichedTemplateLookup[templateId || ''];
                        return template?.sections || [];
                      }
                      return [];
                    })()}
                  />
                  
                  {/* Export Button */}
                  <CsvExportButton
                    onExportAll={() => handleExportGroup(group, selectedColumns, false)}
                    onExportSelected={groupSelectedRows.size > 0 
                      ? () => handleExportGroup(group, selectedColumns, true) 
                      : undefined
                    }
                    selectedCount={groupSelectedRows.size}
                    title="Export folder to CSV"
                  />
                </>
              )}
            </div>

            {/* Table - only render if not collapsed */}
            {!isCollapsed && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <MainTableWidget 
                  objectData={transformCardsToTableData(
                    group.cards, 
                    itemType,
                    undefined,
                    0,
                    currentUserId,
                    blueprintsMap,
                    folderPathLookup
                  ).map((item) => {
                    if (item.type === 'row') {
                      const card = group.cards.find(c => c.id === item.key);
                      if (card) {
                        // Determine border color based on shadow mode
                        let borderColor: string | undefined;
                        
                        if (shadowMode === "folder") {
                          // For folder mode, use original folder color logic
                          borderColor = (card as any).subfolderColor || (card as any).folderColor;
                        } else {
                          // For other modes, use getShadowProps
                          const shadowProps = getShadowProps(card, shadowMode);
                          borderColor = shadowProps.shadowColor;
                        }
                        
                        return {
                          ...item,
                          borderColor: borderColor,
                        };
                      }
                    }
                    return item;
                  })}
                  selectedColumns={selectedColumns}
                  rawItems={groupRawItems}
                  fieldMetadata={fieldMetadata}
                  onCellValueChange={onCellValueChange}
                  onRowClick={onCardClick ? (rowKey) => {
                    const card = group.cards.find(c => c.id === rowKey);
                    if (card) onCardClick(card);
                  } : undefined}
                  modifiedItems={modifiedItems}
                  modifiedCells={modifiedCells}
                  onSaveItem={onSaveItem}
                  onDiscardItem={onDiscardItem}
                  selectedRows={groupSelectedRows}
                  onSelectedRowsChange={(selectedRows) => handleSelectedRowsChange(group.folderId, selectedRows)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
